// Status values that count as "Current" for a case module.
// Active/Outstanding (or module-equivalent) and blank/null statuses are
// Current; every other value (Completed, Lapsed, Closed, etc.) is Non-current.
//
// NOTE on `liability`: casing below ("Active", "Outstanding") mirrors the
// Asset module's confirmed Title Case values, but has NOT been verified
// against a live GET /case/liability/customer/:id response. Verify and
// correct this before relying on it — a casing mismatch will silently
// return zero "current" records since the backend does a case-sensitive
// Op.in match.
// NOTE on `policy`: casing below ("Active", "In Force") is an UNVERIFIED
// placeholder — the doc for this module guessed lowercase ('in force',
// 'applied for') with no evidence anywhere in the codebase, and Asset's own
// doc guessed wrong before (assumed uppercase, real data was Title Case).
// Inspect a live GET /case/policy/customer/:id response and correct this
// before relying on it.
export const CURRENT_CASE_STATUSES: Record<string, string[]> = {
  asset: ["Active", "Pending", ""],
  liability: ["Active", "Outstanding", ""],
  policy: ["Active", "In Force", "Applied For", ""],
};

// Comma-separated list suitable for the `status` query param on the case API
// (e.g. GET /case/asset/customer/:id?status=Active,Pending,)
export const getCurrentStatusParam = (
  module: keyof typeof CURRENT_CASE_STATUSES = "asset"
): string => CURRENT_CASE_STATUSES[module].join(",");

// Normalises a case_type string for comparison: lowercased, hyphens and
// underscores replaced with spaces, and trimmed.  Keeps the filter resilient
// to DB values like "Unit-linked", "unit_linked", or "Unit Linked" all
// matching the same route segment.
export const normalizeCaseType = (s: string): string =>
  (s ?? "").toLowerCase().replace(/[-_]/g, " ").trim();

// Normalises a status string for comparison: same rules as normalizeCaseType.
// Lets "ACTIVE", "active", "Active", "IN_FORCE", "in-force", "In Force"
// all compare equal to the canonical values in CURRENT_CASE_STATUSES.
export const normalizeStatus = (s: string): string =>
  (s ?? "").toLowerCase().replace(/[-_]/g, " ").trim();

// Returns true when `status` normalises to one of the current-case statuses
// for the given module.  Case and separator characters are ignored on both sides.
export const isCurrentStatus = (
  module: keyof typeof CURRENT_CASE_STATUSES,
  status: string
): boolean => {
  const n = normalizeStatus(status);
  return CURRENT_CASE_STATUSES[module].some((s) => normalizeStatus(s) === n);
};

// Extracts a status string from a case item regardless of which field the
// sub-type uses.  Investment assets expose `investment_status`; all other
// sub-types expose a generic `status` field.
export const getItemStatus = (item: any): string =>
  item?.investment_status ?? item?.status ?? "";
